import { Descriptions } from "@/components/v2";
import { Button, Card } from "@rumbledirect/ui";
import { Empty, Image, Tag, type DescriptionsProps } from "antd";
import { Link } from "react-router-dom";
import type { ServicePartner } from "@rumble-it/common-lib/types/models/ServicePartner.interface";
import dayjs from "dayjs";
import { EditOutlined } from "@ant-design/icons";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser.ts";
import { useMemo } from "react";
import { useHasRole } from "@/lib/helpers/roles";
import { useQuery } from "@tanstack/react-query";
import { fetchDwProAccountById } from "@/lib/api/resources/servicePartners";

interface ServicepartnerDetailCardProps {
  id: string;
  data?: ServicePartner;
  loading?: boolean;
}

const supportLabelMap: Record<string, string> = {
  specialist: "Specialist",
  certified: "Erkend",
  supported: "Ondersteund",
  excluded: "Uitgesloten",
};

function supportLabel(support: string): string {
  return supportLabelMap[support] ?? support;
}

export const ServicepartnerDetailCard = ({
  id,
  data,
  loading,
}: ServicepartnerDetailCardProps) => {
  const currentUser = useCurrentUser();

  const hasUpdatePreferredPartnerPermission = useMemo(
    () =>
      currentUser?.permissions?.includes(
        "admin_service_partner_preferred_partner_setting_preferences_read",
      ) ?? false,
    [currentUser],
  );

  const hasAdminRole = useHasRole("ROLE_OIDC_ADMIN");

  const dwProAccountId = useMemo(() => {
    const value = data?.dwProAccount;

    if (!value) return null;

    return value.includes("/") ? (value.split("/").pop() ?? null) : value;
  }, [data?.dwProAccount]);

  const dwProAccountQuery = useQuery({
    queryKey: ["dw-pro-account", dwProAccountId],
    queryFn: () => fetchDwProAccountById(dwProAccountId as string),
    enabled: hasAdminRole && !!dwProAccountId,
  });

  return (
    <Card
      loading={!!loading}
      title={data?.name}
      extra={
        <Link to={`/service-partners/partners/${id}/edit`}>
          <Button icon={<EditOutlined />} />
        </Link>
      }
    >
      {(() => {
        const items: DescriptionsProps["items"] = [
          { label: "Groep Naam", children: data?.groupName ?? null },
          { label: "Naam", children: data?.name ?? null },
          { label: "E-mail", children: data?.email ?? null },
          { label: "Telefoonnummer", children: data?.telephone ?? null },
          { label: "Website", children: data?.website ?? null },
          {
            label: "Adres",
            children: data?.address?.street
              ? `${data.address.street} ${data.address.streetNumber || ""}${data.address.streetNumberAddition || ""}, ${data.address.postalCode} ${data.address.city}`
              : null,
          },
          {
            label: "Postadres",
            children: data?.postalAddress?.street
              ? `${data.postalAddress.street} ${data.postalAddress.streetNumber || ""}${data.postalAddress.streetNumberAddition || ""}, ${data.postalAddress.postalCode} ${data.postalAddress.city}`
              : null,
          },
          {
            label: "Externe referentie",
            children: data?.externalReference ?? null,
          },
          { label: "Gepubliceerd", children: data?.published ? "Ja" : "Nee" },
          ...(hasUpdatePreferredPartnerPermission
            ? [
                {
                  label: "Preferred service partner",
                  children: data?.preferredServicePartner ? "Ja" : "Nee",
                },
              ]
            : []),
          {
            label: "Merk-ondersteuning",
            children:
              data?.brandSupports && data.brandSupports.length > 0 ? (
                <>
                  {data.brandSupports.map((bs) => (
                    <Tag key={bs.brand["@id"]}>
                      {bs.brand.brand ?? bs.brand["@id"]} —{" "}
                      {supportLabel(bs.support)}
                    </Tag>
                  ))}
                </>
              ) : null,
          },
          {
            label: "Tags",
            children:
              data?.tags && data.tags.length > 0 ? (
                <>
                  {data.tags.map((tag) => (
                    <Tag key={tag["@id"]}>{tag.name ?? tag["@id"]}</Tag>
                  ))}
                </>
              ) : null,
          },
          {
            label: "Gesloten dagen",
            children:
              data?.openingHoursExceptionDates &&
              data.openingHoursExceptionDates.length > 0 ? (
                <>
                  {data.openingHoursExceptionDates.map((date) => (
                    <Tag key={date}>{dayjs(date).format("DD-MM-YYYY")}</Tag>
                  ))}
                </>
              ) : null,
          },
          {
            label: "Headerafbeelding",
            children:
              data?.headerImage && data?.headerImage?.contentUrl ? (
                <Image
                  width={240}
                  src={data.headerImage.contentUrl}
                  alt="Headerafbeelding"
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Geen afbeelding"
                />
              ),
          },
          {
            label: "Logo",
            children:
              data?.logoImage && data?.logoImage?.contentUrl ? (
                <Image width={240} src={data.logoImage.contentUrl} alt="Logo" />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Geen afbeelding"
                />
              ),
          },
          // DwProAccount display (admin only)
          ...(hasAdminRole
            ? [
                {
                  label: "DwPro account",
                  children:
                    dwProAccountQuery.data?.title ?? data?.dwProAccount ?? "-",
                },
              ]
            : []),
        ];
        return <Descriptions items={items} />;
      })()}
    </Card>
  );
};

export default ServicepartnerDetailCard;
